import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { resolveMediaUrl } from "../lib/media";
import {
  House, SoccerBall, Megaphone, CalendarBlank, SignOut, List, X, UserCircle
} from "@phosphor-icons/react";

const NAV = [
  { to: "/parent", label: "Home", icon: House, end: true },
  { to: "/parent/announcements", label: "News", icon: Megaphone },
  { to: "/parent/schedule", label: "Schedule", icon: CalendarBlank },
  { to: "/parent/profile", label: "Profile", icon: UserCircle },
];

export default function ParentLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    const load = () => api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    load();
    const onUpdate = () => load();
    window.addEventListener("wahdat:settings-updated", onUpdate);
    return () => window.removeEventListener("wahdat:settings-updated", onUpdate);
  }, []);

  function handleLogout() { logout(); navigate("/login"); }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F5]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0a0a0a] border-b border-neutral-900 h-16 flex items-center px-4 sm:px-6">
        <div className="flex items-center gap-3 flex-1">
          {settings.academy_logo_url ? (
            <img src={resolveMediaUrl(settings.academy_logo_url)} alt="logo" className="w-10 h-10 object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-md bg-[#D90429] flex items-center justify-center"><SoccerBall size={22} weight="fill" color="#fff" /></div>
          )}
          <div>
            <div className="font-display font-bold text-base uppercase tracking-tight leading-none">{settings.academy_name || "Wahdat FC"}</div>
            <div className="overline">Parent Portal</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`parent-nav-${it.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold ${isActive ? "bg-[#D90429] text-white" : "text-neutral-400 hover:text-white hover:bg-neutral-900"}`}
            >
              <it.icon size={16} weight="bold" /> {it.label}
            </NavLink>
          ))}
          <button onClick={handleLogout} data-testid="parent-logout" className="ml-2 flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-neutral-400 hover:text-white hover:bg-neutral-900">
            <SignOut size={16} weight="bold" /> Log Out
          </button>
        </nav>
        <button className="md:hidden p-2 rounded-md hover:bg-neutral-900" onClick={() => setDrawer(true)} data-testid="parent-menu-btn">
          <List size={22} weight="bold" />
        </button>
      </header>

      {drawer && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setDrawer(false)}>
          <div className="absolute right-0 top-0 h-full w-72 bg-[#0a0a0a] border-l border-neutral-900 p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="overline">Parent</div>
              </div>
              <button onClick={() => setDrawer(false)} className="p-2 rounded-md hover:bg-neutral-900"><X size={20} weight="bold" /></button>
            </div>
            {NAV.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setDrawer(false)}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold my-0.5 ${isActive ? "bg-[#D90429] text-white" : "text-neutral-400 hover:text-white hover:bg-neutral-900"}`}>
                <it.icon size={18} weight="bold" /> {it.label}
              </NavLink>
            ))}
            <button onClick={handleLogout} className="w-full mt-3 flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-neutral-400 hover:text-white hover:bg-neutral-900 border-t border-neutral-900 pt-4">
              <SignOut size={18} weight="bold" /> Log Out
            </button>
          </div>
        </div>
      )}

      <main className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto"><Outlet /></main>
    </div>
  );
}
